import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import * as Sentry from '@sentry/react-native';
import { noCase } from 'change-case';
import { getIosPushNotificationServiceEnvironmentAsync } from 'expo-application';
import * as Clipboard from 'expo-clipboard';
import * as Linking from 'expo-linking';
import { getDevicePushTokenAsync } from 'expo-notifications';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { ListRenderItemInfo, Modal, Pressable, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import useAsyncEffect from 'use-async-effect';

import { Alert } from '~/components/alert';
import { Button } from '~/components/button';
import { ArrowForwardOutlineIcon } from '~/components/icon';
import {
    List,
    ListItem,
    ListItemDescription,
    ListItemDivider,
    ListItemPane,
    ListItemPaneContent,
    ListItemTitle
} from '~/components/list';
import { ScreenContainer } from '~/components/screen';
import { Text } from '~/components/text';
import { useAppConfig } from '~/contexts/app-config';
import { useAuth } from '~/contexts/auth';
import { useNetworkServiceState } from '~/contexts/network-service-state';
import { AppNavigatorParamList } from '~/navigator/app-navigator';
import { getDebugMode, isNative, isValidString, setDebugMode } from '~/utils';

type DeveloperNavigation = StackNavigationProp<AppNavigatorParamList>;

export const DeveloperOptionsScreenOptions = {
    title: 'Developer options',
    headerShown: true
};

const bool = (value: boolean | null) => (value === null ? '-' : value ? 'yes' : 'no');

const DeveloperOptionPushNotifications: React.FC = () => {
    const [token, setToken] = useState<string>('');
    const [environment, setEnvironment] = useState<string>('');

    useEffect(() => {
        (async () => {
            if (isNative()) {
                setToken((await getDevicePushTokenAsync()).data);
            }
            setEnvironment((await getIosPushNotificationServiceEnvironmentAsync()) ?? '');
        })();
    }, []);

    return (
        <ListItemPane>
            <ListItemTitle>Push notifications</ListItemTitle>
            <ListItemDescription>Device push token</ListItemDescription>
            <ListItemPaneContent>
                {isValidString(token) ? (
                    <TouchableOpacity
                        onPress={() =>
                            Alert.alert('Push notifications', 'Set push notifications state', [
                                {
                                    text: 'Open settings',
                                    onPress: async () => Linking.openSettings()
                                },
                                {
                                    text: 'Copy token',
                                    onPress: async () => Clipboard.setString(token)
                                },
                                { text: 'Cancel' }
                            ])
                        }
                    >
                        <Text.SYSTEM>{`${token}`}</Text.SYSTEM>
                    </TouchableOpacity>
                ) : (
                    <Text.SYSTEM>Not available on simulator</Text.SYSTEM>
                )}
                {isValidString(environment) ? <Text.P3>{environment}</Text.P3> : null}
            </ListItemPaneContent>
        </ListItemPane>
    );
};

const DeveloperOptionReachability: React.FC = () => {
    const { reachability, getAverageLatency } = useNetworkServiceState();
    const { config } = useAppConfig();
    return (
        <ListItemPane>
            <ListItemTitle>API reachability</ListItemTitle>
            <ListItemDescription>Backend API status and latency</ListItemDescription>
            <ListItemPaneContent>
                <Text.SYSTEM>
                    Service host: {config.host}
                    {'\n'}
                    Service state: {noCase((reachability as string) ?? '-')}
                    {'\n'}
                    Average latency: {getAverageLatency()?.toFixed(1)} ms
                </Text.SYSTEM>
            </ListItemPaneContent>
        </ListItemPane>
    );
};

const DeveloperOptionStorybook: React.FC = () => {
    const { navigate } = useNavigation<DeveloperNavigation>();
    return (
        <ListItem
            title="Storybook"
            description="View app components"
            accessory={() => <ArrowForwardOutlineIcon />}
            onPress={() => navigate('developer-storybook')}
        />
    );
};

const DeveloperOptionBiometricAuth: React.FC = () => {
    const { biometric } = useAuth();

    const methods = { 1: 'Touch ID', 2: 'Face ID', 3: 'Iris' };
    const typeNames = biometric.types.map(type => methods[type]).join(',');

    return (
        <ListItem
            title="Biometric authentication"
            description={`Available: ${bool(biometric.available)} (${typeNames}), enabled: ${bool(biometric.enabled)}`}
            onPress={() => {
                Alert.alert('Local authentication', 'Set local authentication state', [
                    {
                        text: 'Enable',
                        onPress: async () => biometric.enable()
                    },
                    {
                        text: 'Disable',
                        onPress: async () => biometric.disable()
                    },
                    {
                        text: 'Reset',
                        onPress: async () => biometric.reset()
                    },
                    { text: 'Cancel' }
                ]);
            }}
        />
    );
};

const DeveloperOptionPinAuth: React.FC = () => {
    const { pin } = useAuth();

    return (
        <ListItem
            title="PIN authentication"
            description={`Enabled: ${bool(pin.enabled)}`}
            onPress={() => {
                Alert.alert('PIN authentication', 'Set local PIN authentication state', [
                    {
                        text: 'Reset',
                        onPress: async () => pin.reset()
                    },
                    { text: 'Cancel' }
                ]);
            }}
        />
    );
};

const DeveloperUserIdentity: React.FC = () => {
    const { identity } = useAuth();
    const [username, setUsername] = useState<string | null>(null);

    useAsyncEffect(async () => setUsername(await identity.get()), []);

    return (
        <ListItem
            title="Current user"
            description={`Username: ${username ?? '-'}`}
            onPress={() => {
                if (username !== null) {
                    Alert.alert('Reset current user', `Reset current user (${username}) identity`, [
                        {
                            text: 'Reset',
                            onPress: async () => {
                                await identity.clear();
                                setUsername('-');
                            }
                        },
                        { text: 'Cancel' }
                    ]);
                }
            }}
        />
    );
};

const DeveloperOptionPasswordLogin: React.FC = () => {
    const { remote } = useAuth();

    const [visible, setVisible] = useState<boolean>(false);
    const [username, setUsername] = useState<string>();
    const [password, setPassword] = useState<string>();

    const handleAuthenticate = async () => {
        if (username?.length && password?.length) {
            try {
                const result = await remote.login(username, password);
                if (result.success) {
                    result.commit();
                } else {
                    Alert.alert(`Login failed: ${result.reason}`);
                }
            } finally {
                setVisible(false);
            }
        }
    };
    const handleClose = () => {
        setUsername(undefined);
        setPassword(undefined);
        setVisible(false);
    };

    return (
        <>
            <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
                <Pressable style={passwordLoginStyles.backdrop} onPress={handleClose}>
                    <Pressable style={passwordLoginStyles.card} onPress={() => undefined}>
                        <Text.H3>Enter credentials</Text.H3>
                        <Text.P2>Please enter your username and password to authenticate</Text.P2>
                        <TextInput
                            autoFocus
                            autoCorrect={false}
                            autoCapitalize="none"
                            placeholder="Username"
                            value={username ?? ''}
                            onChangeText={setUsername}
                            style={passwordLoginStyles.input}
                        />
                        <TextInput
                            autoCorrect={false}
                            autoCapitalize="none"
                            placeholder="Password"
                            secureTextEntry
                            value={password ?? ''}
                            onChangeText={setPassword}
                            style={passwordLoginStyles.input}
                        />
                        <View style={passwordLoginStyles.buttons}>
                            <Button type="secondary" label="Cancel" onPress={handleClose} />
                            <Button
                                type="primary"
                                label="Login"
                                disabled={!username || !password}
                                onPress={handleAuthenticate}
                            />
                        </View>
                    </Pressable>
                </Pressable>
            </Modal>

            <ListItem
                title="Password authentication"
                description="Login with username and password"
                onPress={() => setVisible(true)}
            />
        </>
    );
};

const passwordLoginStyles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 16
    },
    card: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 16,
        gap: 8
    },
    input: {
        borderWidth: 1,
        borderColor: '#ccc',
        borderRadius: 4,
        padding: 8,
        marginVertical: 4
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
        marginTop: 8
    }
});

const DeveloperOptionLocalStorage: React.FC = () => {
    const { navigate } = useNavigation<DeveloperNavigation>();
    return (
        <ListItem
            title="Local storage"
            description="View local storage items"
            accessory={() => <ArrowForwardOutlineIcon />}
            onPress={() => navigate('developer-local-storage')}
        />
    );
};

const DeveloperOptionPushNotificationsLog: React.FC = () => {
    const { navigate } = useNavigation<DeveloperNavigation>();
    return (
        <ListItem
            title="Push notifications log"
            description="Recent received push notification payloads (debug mode)"
            accessory={() => <ArrowForwardOutlineIcon />}
            onPress={() => navigate('developer-push-notifications-log')}
        />
    );
};

const DeveloperOptionDebugMode: React.FC = () => {
    const [debug, setDebug] = useState<boolean>(getDebugMode());

    useEffect(() => setDebugMode(debug), [debug]);

    return (
        <ListItem
            title="Debug mode"
            description={`Debug mode: ${bool(debug)}`}
            onPress={() =>
                Alert.alert('Debug', 'Enable debug mode?', [
                    { text: 'Enable', onPress: () => setDebug(true) },
                    { text: 'Disable', onPress: () => setDebug(false) },
                    { text: 'Cancel' }
                ])
            }
        />
    );
};

const DeveloperOptionSentry: React.FC = () => {
    return (
        <ListItem
            title="Sentry"
            description="Test Sentry.io integration"
            onPress={() =>
                Alert.alert('Sentry.io', 'Test capturing Sentry events', [
                    {
                        text: 'Generate JS error',
                        onPress: () => {
                            throw new Error('Sentry test');
                        }
                    },
                    { text: 'Generate native crash', onPress: () => Sentry.nativeCrash() },
                    { text: 'Cancel' }
                ])
            }
        />
    );
};

export const Developer: React.FC = () => {
    const { bottom } = useSafeAreaInsets();
    return (
        <ScreenContainer style={{ paddingBottom: bottom }}>
            <List<React.FC>
                data={
                    [
                        DeveloperOptionReachability,
                        isNative() ? DeveloperOptionPushNotifications : null,
                        DeveloperUserIdentity,
                        DeveloperOptionPasswordLogin,
                        DeveloperOptionPinAuth,
                        DeveloperOptionBiometricAuth,
                        DeveloperOptionLocalStorage,
                        isNative() ? DeveloperOptionPushNotificationsLog : null,
                        DeveloperOptionStorybook,
                        DeveloperOptionDebugMode,
                        DeveloperOptionSentry
                    ].filter(item => !!item) as FunctionComponent[]
                }
                ItemSeparatorComponent={ListItemDivider}
                renderItem={({ item: Component }: ListRenderItemInfo<FunctionComponent>) => <Component />}
            />
        </ScreenContainer>
    );
};
